import { Container, SelectEvent } from '@acies/core';
import HTMLSelectListEvent from './html-select-list-event';
import HTMLSelectListItemRenderer from './html-select-list-item-renderer';

/**
 * The HTMLSelectList implements selection logic when added to the DOM as html.
 *
 * When children are instances of the ac-html-select-list-item-renderer class or extended sub classes, we get full selection support, so clicking a child will de-select another currently selected child.
 *
 * When selection changes by clicking a child or de-selecting, a HTMLSelectListEvent will be dispatched, with the selectedItem property set to the payload property of the child.
 */
export default class HTMLSelectList extends Container {
	public get selectedItem(): unknown {
		return this.#selectedItem;
	}

	#mutationObserver: MutationObserver;
	#selectedItem: unknown;
	public constructor() {
		super();
		/** When the children are being dynamically renderer, we listen for DOM changes so we can reset the selectedItem property and dispatch a HTMLSelectListEvent with a null payload property. */
		this.#mutationObserver = new MutationObserver(this.#mutationHappened.bind(this));
	}

	protected override initialize(): void {
		super.initialize();
		this.addEventListener(SelectEvent.SELECTED_CHANGED, this.#itemRendererSelectedChanged as EventListener);
		// childList: true indicates that we are only interested in direct child mutations, not grand children.
		this.#mutationObserver.observe(this, { childList: true });
	}

	#dispatchSelectionChangedEvent(): void {
		this.dispatchEvent(new HTMLSelectListEvent(this.selectedItem));
	}

	#itemRendererSelectedChanged(event: SelectEvent<unknown>): void {
		// we stop the event from bubbling further up the DOM and stops it here
		// so lists within lists don't recieve child list events.
		event.stopImmediatePropagation();
		this.#updateAndNotifySelectedItem(event);
	}

	#mutationHappened(): void {
		this.#selectedItem = null;
		this.#dispatchSelectionChangedEvent();
	}

	#updateAndNotifySelectedItem(event: SelectEvent<unknown>): void {
		for (const renderer of this.children) {
			// we check if the children are of the correct class,
			// so we are sure they have the required properties.
			if (renderer instanceof HTMLSelectListItemRenderer) {
				this.#updateRendererSelectedProperty(renderer, event);
				this.#updateSelectedItemPropertyAndNotify(renderer, event);
			}
		}
	}

	/** When a child is selected, de-select all other children. */
	#updateRendererSelectedProperty(renderer: HTMLSelectListItemRenderer, event: SelectEvent<unknown>): void {
		if (renderer !== event.target) {
			renderer.selected = false;
		}
	}

	#updateSelectedItemPropertyAndNotify(renderer: HTMLSelectListItemRenderer, event: SelectEvent<unknown>): void {
		if (renderer === event.target) {
			if (event.selected) {
				this.#selectedItem = event.payload;
			} else {
				this.#selectedItem = null;
			}
			this.#dispatchSelectionChangedEvent();
		}
	}
}
customElements.define('ac-html-select-list', HTMLSelectList);
